/* Kneepaw — composants ajoutés à Dawn (sans dépendance). */

(() => {
  const formatMoney = (cents, format) => {
    const fmt = format || '{{amount_with_comma_separator}} €';
    const value = Number(cents) || 0;
    const fixed = (precision, thousands, decimal) => {
      const [int, dec] = (value / 100).toFixed(precision).split('.');
      const withThousands = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return dec ? `${withThousands}${decimal}${dec}` : withThousands;
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fixed(0, ',', '.');
        case 'amount_with_comma_separator': return fixed(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fixed(0, '.', ',');
        case 'amount_with_apostrophe_separator': return fixed(2, "'", '.');
        case 'amount_with_space_separator': return fixed(2, ' ', ',');
        default: return fixed(2, ',', '.');
      }
    });
  };

  /* ---------- Offres par quantité ---------- */
  class KpOffers extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.formId);
      this.moneyFormat = this.dataset.moneyFormat;
      try {
        this.variants = JSON.parse(this.querySelector('[data-kp-variants]').textContent);
      } catch (e) {
        this.variants = [];
      }
      this.offers = Array.from(this.querySelectorAll('.kp-offer'));
      this.addEventListener('change', this.onChange.bind(this));
      this.onSubmit = this.onSubmit.bind(this);
      document.addEventListener('submit', this.onSubmit, true);
      this.refresh();
    }

    disconnectedCallback() {
      document.removeEventListener('submit', this.onSubmit, true);
    }

    get selected() {
      return this.offers.find((offer) => offer.querySelector('.kp-offer__input').checked) || this.offers[0];
    }

    variantById(id) {
      return this.variants.find((variant) => String(variant.id) === String(id));
    }

    onChange(event) {
      if (event.target.classList.contains('kp-offer__input')) {
        // Recopie la taille déjà choisie vers la nouvelle offre.
        const previous = this.querySelector('.kp-offer.is-selected [data-kp-unit-select]');
        const next = event.target.closest('.kp-offer').querySelectorAll('[data-kp-unit-select]');
        if (previous) next.forEach((select) => { if (!select.dataset.touched) select.value = previous.value; });
      }
      if (event.target.matches('[data-kp-unit-select]')) event.target.dataset.touched = 'true';
      this.refresh();
    }

    unitVariantIds(offer) {
      const selects = offer.querySelectorAll('[data-kp-unit-select]');
      if (selects.length) return Array.from(selects, (select) => select.value);
      const qty = Number(offer.dataset.quantity) || 1;
      return Array.from({ length: qty }, () => this.dataset.defaultVariant);
    }

    refresh() {
      this.offers.forEach((offer) => {
        const isSelected = offer === this.selected;
        offer.classList.toggle('is-selected', isSelected);
        const discount = Number(offer.dataset.discount) || 0;
        const amountOff = Number(offer.dataset.amountOff) || 0;
        let total = 0;
        let compare = 0;
        let allAvailable = true;
        this.unitVariantIds(offer).forEach((id) => {
          const variant = this.variantById(id);
          if (!variant) return;
          if (!variant.available) allAvailable = false;
          total += Math.round(variant.price * (100 - discount) / 100);
          compare += amountOff ? variant.price : Math.max(variant.compare_at_price || 0, variant.price);
        });
        total = Math.max(0, total - amountOff);
        const priceEl = offer.querySelector('[data-kp-price]');
        const compareEl = offer.querySelector('[data-kp-compare]');
        const saveEl = offer.querySelector('[data-kp-save]');
        if (priceEl) priceEl.textContent = formatMoney(total, this.moneyFormat);
        if (compareEl) {
          compareEl.textContent = compare > total ? formatMoney(compare, this.moneyFormat) : '';
        }
        if (saveEl) {
          saveEl.hidden = compare <= total;
          saveEl.textContent = `${saveEl.dataset.label} ${formatMoney(compare - total, this.moneyFormat)}`;
        }
        offer.dataset.available = allAvailable;
        if (isSelected) {
          this.currentTotal = total;
          this.currentAvailable = allAvailable;
        }
      });

      const firstId = this.unitVariantIds(this.selected)[0];
      if (this.form) {
        const idInput = this.form.querySelector('input[name="id"]');
        if (idInput && firstId) idInput.value = firstId;
        const button = this.form.querySelector('[type="submit"]');
        if (button) {
          const label = button.querySelector('span');
          if (!this.currentAvailable) {
            button.setAttribute('disabled', 'disabled');
            if (label) label.textContent = window.variantStrings?.soldOut || 'Épuisé';
          } else {
            button.removeAttribute('disabled');
            if (label && this.dataset.buttonLabel) label.textContent = this.dataset.buttonLabel;
          }
        }
      }

      this.dispatchEvent(
        new CustomEvent('kp:offer-change', {
          bubbles: true,
          detail: { total: this.currentTotal, formatted: formatMoney(this.currentTotal, this.moneyFormat), firstVariantId: firstId },
        })
      );
    }

    setFirstUnit(variantId) {
      const select = this.selected.querySelector('[data-kp-unit-select]');
      if (select) {
        select.value = variantId;
        select.dataset.touched = 'true';
      } else {
        this.dataset.defaultVariant = variantId;
      }
      this.refresh();
    }

    buildItems() {
      const counts = new Map();
      this.unitVariantIds(this.selected).forEach((id) => counts.set(id, (counts.get(id) || 0) + 1));
      // Shopify place en tête du panier la dernière ligne ajoutée :
      // les cadeaux d'abord, le produit principal en dernier pour qu'il apparaisse en haut.
      const gifts = Array.from(this.selected.querySelectorAll('[data-kp-gift]'), (gift) => ({
        id: Number(gift.dataset.kpGift),
        quantity: Number(gift.dataset.quantity) || 1,
      }));
      const main = Array.from(counts, ([id, quantity]) => ({ id: Number(id), quantity }));
      return [...gifts, ...main];
    }

    onSubmit(event) {
      if (!this.form || event.target !== this.form) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      event.stopPropagation();

      const button = this.form.querySelector('[type="submit"]');
      const spinner = this.form.querySelector('.loading__spinner');
      const errorWrapper = this.form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
      const errorMessage = errorWrapper?.querySelector('.product-form__error-message');
      if (!button || button.getAttribute('aria-disabled') === 'true') return;

      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
      spinner?.classList.remove('hidden');
      errorWrapper?.setAttribute('hidden', '');

      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const body = { items: this.buildItems() };
      if (cart) {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
        cart.setActiveElement(document.activeElement);
      }

      fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            if (errorWrapper && errorMessage) {
              errorMessage.textContent = response.description || response.message;
              errorWrapper.removeAttribute('hidden');
            }
            return;
          }
          if (!cart) {
            window.location = window.routes.cart_url;
            return;
          }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'kp-offers',
              productVariantId: body.items[body.items.length - 1].id,
              cartData: response,
            });
          }
          // La notification Dawn attend la clé de la ligne ajoutée ; le tiroir non.
          if (cart.tagName === 'CART-NOTIFICATION' && response.items) response.key = response.items[0].key;
          cart.renderContents(response);
          cart.classList.remove('is-empty');
        })
        .catch((error) => {
          console.error(error);
          if (errorWrapper && errorMessage) {
            errorMessage.textContent = window.cartStrings?.error || 'Une erreur est survenue. Réessayez.';
            errorWrapper.removeAttribute('hidden');
          }
        })
        .finally(() => {
          button.classList.remove('loading');
          button.removeAttribute('aria-disabled');
          spinner?.classList.add('hidden');
        });
    }
  }
  if (!customElements.get('kp-offers')) customElements.define('kp-offers', KpOffers);

  /* ---------- Barre d'achat collante ---------- */
  class KpStickyAtc extends HTMLElement {
    connectedCallback() {
      const suffix = this.dataset.sectionSuffix || '__main';
      this.form =
        document.querySelector(`form[id^="product-form-"][id$="${suffix}"]`) ||
        document.querySelector('product-info form[id^="product-form-"]');
      this.select = this.querySelector('select');
      this.priceEl = this.querySelector('[data-kp-sticky-price]');
      this.button = this.querySelector('[data-kp-sticky-button]');
      this.offers = this.form ? document.querySelector(`kp-offers[data-form-id="${this.form.id}"]`) : null;
      if (!this.form) return;

      const target = this.form.querySelector('[type="submit"]') || this.form;
      this.observer = new IntersectionObserver(([entry]) => {
        const scrolledPast = entry.boundingClientRect.top < 0;
        const visible = !entry.isIntersecting && scrolledPast;
        this.classList.toggle('is-visible', visible);
        document.body.classList.toggle('kp-has-sticky-atc', visible);
        this.setAttribute('aria-hidden', String(!visible));
      });
      this.observer.observe(target);

      this.button?.addEventListener('click', () => {
        const submit = this.form.querySelector('[type="submit"]');
        if (submit?.hasAttribute('disabled')) {
          this.form.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        this.form.requestSubmit(submit || undefined);
      });

      this.select?.addEventListener('change', () => this.syncVariant(this.select.value));

      document.addEventListener('kp:offer-change', (event) => {
        if (this.priceEl && event.detail.formatted) this.priceEl.textContent = event.detail.formatted;
        if (this.select && event.detail.firstVariantId) this.select.value = event.detail.firstVariantId;
      });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    syncVariant(variantId) {
      if (this.offers) {
        this.offers.setFirstUnit(variantId);
        return;
      }
      const option = this.select.selectedOptions[0]?.dataset.option;
      const scope = this.form.closest('section') || document;
      const radio = Array.from(scope.querySelectorAll('variant-selects input[type="radio"]')).find(
        (input) => input.value === option
      );
      if (radio) {
        radio.click();
        return;
      }
      const dawnSelect = Array.from(scope.querySelectorAll('variant-selects select')).find((select) =>
        Array.from(select.options).some((opt) => opt.value === option)
      );
      if (dawnSelect) {
        dawnSelect.value = Array.from(dawnSelect.options).find((opt) => opt.value === option).value;
        dawnSelect.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      const idInput = this.form.querySelector('input[name="id"]');
      if (idInput) idInput.value = variantId;
    }
  }
  if (!customElements.get('kp-sticky-atc')) customElements.define('kp-sticky-atc', KpStickyAtc);

  /* ---------- Guide des tailles ---------- */
  class KpSizeGuide extends HTMLElement {
    connectedCallback() {
      const dialog = this.querySelector('dialog');
      if (!dialog) return;
      this.querySelector('[data-kp-open]')?.addEventListener('click', () => dialog.showModal());
      this.querySelector('[data-kp-close]')?.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
    }
  }
  if (!customElements.get('kp-size-guide')) customElements.define('kp-size-guide', KpSizeGuide);

  /* ---------- Anneaux de pourcentage ---------- */
  class KpRings extends HTMLElement {
    connectedCallback() {
      const rings = this.querySelectorAll('.kp-ring__value');
      const fill = () =>
        rings.forEach((ring) => {
          const circumference = Number(ring.dataset.circumference);
          const value = Math.min(100, Math.max(0, Number(ring.dataset.value) || 0));
          ring.style.strokeDashoffset = String(circumference * (1 - value / 100));
        });
      if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        fill();
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          fill();
          observer.disconnect();
        }
      }, { threshold: 0.3 });
      observer.observe(this);
    }
  }
  if (!customElements.get('kp-rings')) customElements.define('kp-rings', KpRings);
})();
